import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Search, AlertCircle, Clock, CheckCircle, XCircle, Filter, LayoutGrid, List, Kanban, MapPin, Monitor, DollarSign, X, Trash2, ChevronLeft, ChevronRight, Paperclip, ShieldBan, Ban, Package, Zap, AlertTriangle, ScanLine, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import ScanAssetLookup from '../components/ScanAssetLookup';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { format } from 'date-fns';
import { formatInCST } from '../utils/timezone';
import TicketDetailDialog from '../components/TicketDetailDialog';
import { useViewport } from '../hooks/useViewport';

export default function Tickets() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const navigate = useNavigate();
  const { isDesktop } = useViewport();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(!!searchParams.get('customer_id'));
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || 'closed,resolved',
    status_mode: searchParams.get('status') ? 'is' : 'is_not',
    priority: '',
    priority_mode: 'is',
    customer_id: searchParams.get('customer_id') || '',
    board_id: '',
    support_type: '',
    is_billable: '',
    show_merged: false
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeTarget, setMergeTarget] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    customer_id: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    status: 'open',
    priority: 'medium',
    category: 'other',
    support_type: 'remote',
    board_id: ''
  });
  const [ccEmails, setCcEmails] = useState([]);
  const [ccInput, setCcInput] = useState('');
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [customerAssets, setCustomerAssets] = useState([]);
  const [routingSuggestion, setRoutingSuggestion] = useState(null);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
  });
  const [draggedTicket, setDraggedTicket] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalTickets, setTotalTickets] = useState(0);

  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(15);
  const autoRefreshRef = React.useRef(null);

  useEffect(() => {
    loadTickets();
    loadCustomers();
    loadBoards();
  }, [searchTerm, filters, currentPage, pageSize, sortBy, sortOrder]);

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefreshRef.current) { clearInterval(autoRefreshRef.current); autoRefreshRef.current = null; }
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(() => {
        loadTickets();
      }, refreshInterval * 1000);
    }
    return () => { if (autoRefreshRef.current) clearInterval(autoRefreshRef.current); };
  }, [autoRefresh, refreshInterval]);

  // Reset to page 0 when search, filters, or sort change
  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, filters.status, filters.status_mode, filters.priority, filters.priority_mode, filters.customer_id, filters.board_id, filters.support_type, filters.is_billable, filters.show_merged, sortBy, sortOrder]);

  // Deep-link from nav "New Ticket" action (?new=1): auto-open the existing Create Ticket dialog.
  // Reacts to searchParams (not mount-only) so it also fires when the nav link is clicked while
  // already on /tickets (SPA navigation updates the query string without remounting this page).
  // The 'new' param is stripped immediately (history replace, other params preserved) so closing
  // the dialog and clicking the nav link again produces a fresh value change and reopens it.
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setDialogOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Load assets when customer is selected in the create dialog
  useEffect(() => {
    if (formData.customer_id) {
      api.get('/assets', { params: { customer_id: formData.customer_id, limit: 1000 } })
        .then(res => setCustomerAssets(Array.isArray(res.data) ? res.data : []))
        .catch(() => setCustomerAssets([]));
    } else {
      setCustomerAssets([]);
      setSelectedAssets([]);
    }
  }, [formData.customer_id]);

  // Fetch routing suggestion when selected assets change
  useEffect(() => {
    if (selectedAssets.length > 0) {
      api.post('/tickets/suggest-routing', { asset_ids: selectedAssets.map(a => a.id) })
        .then(res => setRoutingSuggestion(res.data?.suggestion || null))
        .catch(() => setRoutingSuggestion(null));
    } else {
      setRoutingSuggestion(null);
    }
  }, [selectedAssets]);

  const loadTickets = async () => {
    try {
      const params = {
        skip: currentPage * pageSize,
        limit: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (searchTerm) params.search = searchTerm;
      
      // When searching, don't apply default status filter so all tickets are searchable
      // Only apply explicit user-chosen filters
      const applyStatusFilter = searchTerm ? (filters.status && filters.status !== 'closed,resolved') : !!filters.status;
      
      if (applyStatusFilter && filters.status) {
        if (filters.status_mode === 'is_not') {
          params.exclude_status = filters.status;
        } else {
          params.status = filters.status;
        }
      } else if (!searchTerm && filters.status) {
        // Default filter (no search active)
        if (filters.status_mode === 'is_not') {
          params.exclude_status = filters.status;
        } else {
          params.status = filters.status;
        }
      }
      
      // Priority filter with include/exclude mode
      if (filters.priority) {
        if (filters.priority_mode === 'is_not') {
          params.exclude_priority = filters.priority;
        } else {
          params.priority = filters.priority;
        }
      }
      
      if (filters.customer_id) params.customer_id = filters.customer_id;
      if (filters.board_id) params.board_id = filters.board_id;
      if (filters.support_type) params.support_type = filters.support_type;
      if (filters.is_billable !== '') params.is_billable = filters.is_billable === 'true';
      if (filters.show_merged) params.show_merged = true;
      
      const response = await api.get('/tickets', { params });
      const data = response.data;
      // Handle both new paginated format and legacy array format
      if (data && data.tickets) {
        setTickets(data.tickets);
        setTotalTickets(data.total || 0);
      } else if (Array.isArray(data)) {
        setTickets(data);
        setTotalTickets(data.length);
      }
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await api.get('/customers', { params: { limit: 10000, status: 'active' } });
      const custData = response.data;
      setCustomers(Array.isArray(custData) ? custData : custData.customers || []);
    } catch (error) {
      console.error('Failed to load customers');
    }
  };

  const loadBoards = async () => {
    try {
      const response = await api.get('/ticket-boards');
      setBoards(response.data.filter(b => b.is_active));
    } catch (error) {
      console.error('Failed to load boards');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let customerId = formData.customer_id;
      let customerName = '';

      // If creating a new customer, do that first
      if (isNewCustomer) {
        if (!newCustomerData.company_name.trim()) {
          toast.error('Company / Customer name is required');
          return;
        }
        const custRes = await api.post('/customers', {
          company_name: newCustomerData.company_name.trim(),
          contact_name: newCustomerData.contact_name.trim() || undefined,
          email: newCustomerData.email.trim() || undefined,
          phone: newCustomerData.phone.trim() || undefined,
          source: 'manual_ticket',
        });
        customerId = custRes.data.id;
        customerName = custRes.data.company_name;
        toast.success(`Customer "${customerName}" created`);
        // Refresh customer list in background
        loadCustomers();
      } else {
        const customer = customers.find(c => c.id === formData.customer_id);
        customerName = customer?.company_name || '';
      }

      const dataToSubmit = {
        ...formData,
        customer_id: customerId,
        customer_name: customerName,
        // Pre-fill contact fields from new customer if not already set
        contact_person: formData.contact_person || (isNewCustomer ? newCustomerData.contact_name : ''),
        contact_email: formData.contact_email || (isNewCustomer ? newCustomerData.email : ''),
      };

      if (!dataToSubmit.contact_email) delete dataToSubmit.contact_email;
      if (!dataToSubmit.contact_person) delete dataToSubmit.contact_person;
      if (!dataToSubmit.contact_phone) delete dataToSubmit.contact_phone;
      if (!dataToSubmit.description) delete dataToSubmit.description;
      
      const res = await api.post('/tickets', dataToSubmit);
      const newTicketId = res.data?.id;

      // Add CC contacts if any
      if (newTicketId && ccEmails.length > 0) {
        for (const email of ccEmails) {
          try {
            await api.post(`/tickets/${newTicketId}/cc`, { email });
          } catch { /* skip failed CCs silently */ }
        }
      }

      // Add linked assets if any
      if (newTicketId && selectedAssets.length > 0) {
        for (const asset of selectedAssets) {
          try {
            await api.post(`/tickets/${newTicketId}/assets`, { asset_id: asset.id });
          } catch { /* skip failed assets silently */ }
        }
      }

      toast.success('Ticket created successfully');
      setDialogOpen(false);
      resetForm();
      loadTickets();
    } catch (error) {
      const errorMessage = error.response?.data?.detail;
      if (Array.isArray(errorMessage)) {
        toast.error(errorMessage.map(e => e.msg).join(', '));
      } else if (typeof errorMessage === 'string') {
        toast.error(errorMessage);
      } else {
        toast.error('An error occurred while creating the ticket');
      }
      console.error('Ticket creation error:', error.response?.data);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await api.put(`/tickets/${ticketId}`, { status: newStatus });
      toast.success('Ticket status updated');
      loadTickets();
    } catch (error) {
      toast.error('Failed to update ticket status');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      customer_id: '',
      contact_person: '',
      contact_email: '',
      contact_phone: '',
      status: 'open',
      priority: 'medium',
      category: 'other',
      support_type: 'remote',
      board_id: ''
    });
    setCcEmails([]);
    setCcInput('');
    setIsNewCustomer(false);
    setNewCustomerData({ company_name: '', contact_name: '', email: '', phone: '' });
    setSelectedAssets([]);
    setCustomerAssets([]);
    setRoutingSuggestion(null);
  };

  const handleColumnSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to descending
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getSortedAndFilteredTickets = () => {
    // Server handles sorting and filtering — return tickets as-is
    return [...tickets];
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allTicketIds = getSortedAndFilteredTickets().map(t => t.id);
      setSelectedTickets(allTicketIds);
    } else {
      setSelectedTickets([]);
    }
  };

  const handleSelectTicket = (ticketId, checked) => {
    if (checked) {
      setSelectedTickets(prev => [...prev, ticketId]);
    } else {
      setSelectedTickets(prev => prev.filter(id => id !== ticketId));
    }
  };

  const handleBulkDelete = async () => {
// removed console statement
    
    if (selectedTickets.length === 0) {
// removed console statement
      toast.error('Please select at least one ticket to delete');
      return;
    }
    
    // Open confirmation dialog instead of using window.confirm
    setDeleteConfirmOpen(true);
  };

  const confirmBulkDelete = async () => {
// removed console statement
    
    try {
// removed console statement
      const response = await api.post('/tickets/bulk-delete', { ticket_ids: selectedTickets });
// removed console statement
      toast.success(`Successfully deleted ${selectedTickets.length} ticket(s)`);
      setSelectedTickets([]);
      setDeleteConfirmOpen(false);
      loadTickets();
    } catch (error) {
      console.error('Bulk delete error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to delete tickets');
      setDeleteConfirmOpen(false);
    }
  };


  const handleMergeTickets = () => {
    if (selectedTickets.length < 2) {
      toast.error('Please select at least 2 tickets to merge');
      return;
    }
    setMergeDialogOpen(true);
  };

  const confirmMerge = async () => {
    if (!mergeTarget) {
      toast.error('Please select a target ticket');
      return;
    }
    
    try {
      const sourceIds = selectedTickets.filter(id => id !== mergeTarget);
      
      const response = await api.post('/tickets/merge', {
        target_ticket_id: mergeTarget,
        source_ticket_ids: sourceIds,
        close_source_tickets: true,
        add_merge_note: true
      });
      
      toast.success(
        `Merged ${response.data.merged_tickets.length} tickets into ${response.data.target_ticket}. ` +
        `Moved ${response.data.comments_moved} comments, ${response.data.attachments_moved} attachments.`,
        { duration: 5000 }
      );
      
      setSelectedTickets([]);
      setMergeTarget('');
      setMergeDialogOpen(false);
      loadTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge tickets');
    }
  };


  const handleBulkStatusChange = async (newStatus) => {
    if (selectedTickets.length === 0) return;
    
    try {
      await api.post('/tickets/bulk-update-status', {
        ticket_ids: selectedTickets,
        new_status: newStatus
      });
      toast.success(`Updated ${selectedTickets.length} ticket(s) to ${newStatus}`);
      setSelectedTickets([]);
      loadTickets();
    } catch (error) {
      toast.error('Failed to update ticket status');
    }
  };

  const handleBulkSpam = async (reason, blockSender = false) => {
    if (selectedTickets.length === 0) return;
    const label = reason === 'spam' ? 'spam' : 'unsolicited';
    if (!window.confirm(`Mark ${selectedTickets.length} ticket(s) as ${label}${blockSender ? ' and block senders' : ''}? Tickets will be closed.`)) return;
    try {
      await api.post('/tickets/bulk-spam', {
        ticket_ids: selectedTickets,
        reason,
        block_sender: blockSender,
      });
      toast.success(`${selectedTickets.length} ticket(s) marked as ${label}${blockSender ? ' and senders blocked' : ''}`);
      setSelectedTickets([]);
      loadTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark tickets');
    }
  };

  const handleDialogChange = (open) => {
    setDialogOpen(open);
    if (!open) resetForm();
  };

  const handleTicketClick = (ticket) => {
    // On mobile/tablet, navigate to full page instead of dialog
    if (!isDesktop) {
      navigate(`/tickets/${ticket.id}`);
      return;
    }
    setSelectedTicket(ticket);
    setDetailDialogOpen(true);
  };

  const handleTicketUpdate = () => {
    loadTickets();
  };

  const handleDragStart = (e, ticket) => {
    setDraggedTicket(ticket);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedTicket(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    
    if (!draggedTicket) return;
    
    // Don't update if status is the same
    if (draggedTicket.status === newStatus) {
      setDraggedTicket(null);
      return;
    }
    
    // Update ticket status
    try {
      await api.put(`/tickets/${draggedTicket.id}`, { status: newStatus });
      toast.success(`Ticket moved to ${capitalize(newStatus)}`);
      loadTickets();
    } catch (error) {
      toast.error('Failed to update ticket status');
    }
    
    setDraggedTicket(null);
  };

  const getTicketsByStatus = (status) => {
    // Use the sorted and filtered tickets, then filter by status for the board view
    return getSortedAndFilteredTickets().filter(ticket => ticket.status === status);
  };

  const clearFilters = () => {
    setFilters({
      status: 'closed,resolved',
      status_mode: 'is_not',
      priority: '',
      priority_mode: 'is',
      customer_id: '',
      board_id: '',
      support_type: '',
      is_billable: '',
      show_merged: false
    });
  };

  const showAllTickets = () => {
    setFilters({
      status: '',
      status_mode: 'is',
      priority: '',
      priority_mode: 'is',
      customer_id: '',
      board_id: '',
      support_type: '',
      is_billable: '',
      show_merged: false
    });
  };

  const hasActiveFilters = (filters.status && !(filters.status === 'closed,resolved' && filters.status_mode === 'is_not')) || filters.priority || filters.customer_id || filters.board_id || filters.support_type || filters.is_billable || filters.show_merged;
  const isDefaultFilter = filters.status === 'closed,resolved' && filters.status_mode === 'is_not' && !filters.priority && !filters.customer_id && !filters.board_id && !filters.support_type && !filters.is_billable;

  const capitalize = (str) => {
    return str.replace('_', ' ').split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getAssetStatusColor = (s) => ({ active: 'bg-emerald-500', maintenance: 'bg-amber-500', inactive: 'bg-gray-400', retired: 'bg-red-500' }[s] || 'bg-gray-400');
  const getAssetStatusLabel = (s) => ({ active: 'Active', maintenance: 'Maintenance', inactive: 'Inactive', retired: 'Retired' }[s] || 'Unknown');

  const AssetHealthBadge = ({ linkedAssets }) => {
    if (!linkedAssets || linkedAssets.length === 0) return null;
    const worst = linkedAssets.find(a => a.status === 'retired') || linkedAssets.find(a => a.status === 'maintenance') || linkedAssets.find(a => a.status === 'inactive') || linkedAssets[0];
    const worstStatus = worst?.status || 'active';
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-50 border border-gray-200 ml-1" data-testid="asset-health-badge" title={linkedAssets.map(a => `${a.asset_name}: ${getAssetStatusLabel(a.status)}`).join(', ')}>
        <span className={`h-1.5 w-1.5 rounded-full ${getAssetStatusColor(worstStatus)}`} />
        <Package className="h-3 w-3 text-gray-500" />
        <span className="text-gray-600">{linkedAssets.length}</span>
      </span>
    );
  };

  const SlaBadge = ({ ticket }) => {
    if (!ticket.sla_breached) return null;
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 border border-red-200 text-red-700 ml-1" data-testid="sla-breach-badge" title="SLA breached">
        <AlertTriangle className="h-3 w-3" />
        SLA
      </span>
    );
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors',
      medium: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 transition-colors',
      high: 'bg-orange-100 text-orange-800 hover:bg-orange-200 transition-colors',
      critical: 'bg-red-100 text-red-800 hover:bg-red-200 transition-colors'
    };
    return colors[priority] || colors.medium;
  };

  const getStatusIcon = (status) => {
    const icons = {
      open: <AlertCircle className="h-4 w-4" />,
      in_progress: <Clock className="h-4 w-4" />,
      resolved: <CheckCircle className="h-4 w-4" />,
      closed: <XCircle className="h-4 w-4" />
    };
    return icons[status] || icons.open;
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-sky-500/15 text-sky-300 hover:bg-sky-500/25 transition-colors',
      in_progress: 'bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 transition-colors',
      pending: 'bg-violet-500/15 text-violet-300 hover:bg-violet-500/25 transition-colors',
      resolved: 'bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 transition-colors',
      closed: 'bg-white/10 text-[#b1c3ce] hover:bg-white/15 transition-colors'
    };
    return colors[status] || colors.open;
  };

  return (
    <div className="co-page co-scope co-settings ticket-ui space-y-4 sm:space-y-6" data-testid="tickets-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Tickets</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">Manage support tickets and requests</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Auto-refresh controls */}
          <div className="flex items-center gap-1.5 bg-white/5 border border-[#193b4b] rounded-lg px-2 py-1" data-testid="auto-refresh-controls">
            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => loadTickets()} data-testid="refresh-tickets-btn" title="Refresh ticket board">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <div className="h-4 w-px bg-[#193b4b]" />
            <label className="flex items-center gap-1.5 cursor-pointer text-xs text-muted-foreground">
              <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-300 h-3.5 w-3.5" data-testid="auto-refresh-toggle" />
              Auto
            </label>
            {autoRefresh && (
              <select value={refreshInterval} onChange={e => setRefreshInterval(Number(e.target.value))}
                className="text-xs border rounded px-1 py-0.5 bg-white h-6" data-testid="auto-refresh-interval">
                <option value={5}>5s</option>
                <option value={10}>10s</option>
                <option value={15}>15s</option>
                <option value={30}>30s</option>
                <option value={60}>1m</option>
              </select>
            )}
            {autoRefresh && <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" title="Auto-refreshing" />}
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
          <DialogTrigger asChild>
            <Button data-testid="add-ticket-button">
              <Plus className="h-4 w-4 mr-2" />
              Create Ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="co-scope max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Ticket</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="customer_id">Customer *</Label>
                    <button
                      type="button"
                      data-testid="toggle-new-customer"
                      className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      onClick={() => {
                        setIsNewCustomer(!isNewCustomer);
                        if (!isNewCustomer) {
                          setFormData(prev => ({ ...prev, customer_id: '' }));
                        } else {
                          setNewCustomerData({ company_name: '', contact_name: '', email: '', phone: '' });
                        }
                      }}
                    >
                      {isNewCustomer ? '← Select existing customer' : '+ New Customer'}
                    </button>
                  </div>

                  {isNewCustomer ? (
                    <div className="border rounded-lg p-3 bg-blue-50/50 space-y-3" data-testid="new-customer-form">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Company / Customer Name *</Label>
                          <Input
                            data-testid="new-customer-company"
                            value={newCustomerData.company_name}
                            onChange={(e) => setNewCustomerData(prev => ({ ...prev, company_name: e.target.value }))}
                            placeholder="Acme Corp"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Contact Name</Label>
                          <Input
                            data-testid="new-customer-contact"
                            value={newCustomerData.contact_name}
                            onChange={(e) => setNewCustomerData(prev => ({ ...prev, contact_name: e.target.value }))}
                            placeholder="John Smith"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Email</Label>
                          <Input
                            data-testid="new-customer-email"
                            type="email"
                            value={newCustomerData.email}
                            onChange={(e) => setNewCustomerData(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="john@acmecorp.com"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Phone</Label>
                          <Input
                            data-testid="new-customer-phone"
                            value={newCustomerData.phone}
                            onChange={(e) => setNewCustomerData(prev => ({ ...prev, phone: e.target.value }))}
                            placeholder="(555) 123-4567"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Select
                      value={formData.customer_id}
                      onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                      required
                    >
                      <SelectTrigger data-testid="customer-select">
                        <SelectValue placeholder="Select a customer" />
                      </SelectTrigger>
                      <SelectContent className="co-scope ticket-ui">
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.company_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div>
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    data-testid="title-input"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    data-testid="description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_person">Contact Person</Label>
                    <Input
                      id="contact_person"
                      value={formData.contact_person}
                      onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_email">Contact Email</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    />
                  </div>
                </div>

                {/* CC Contacts */}
                <div>
                  <Label>CC Contacts</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="email"
                      placeholder="Add email and press Enter"
                      value={ccInput}
                      onChange={(e) => setCcInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const email = ccInput.trim().toLowerCase();
                          if (email && email.includes('@') && !ccEmails.includes(email)) {
                            setCcEmails(prev => [...prev, email]);
                            setCcInput('');
                          }
                        }
                      }}
                      data-testid="cc-email-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const email = ccInput.trim().toLowerCase();
                        if (email && email.includes('@') && !ccEmails.includes(email)) {
                          setCcEmails(prev => [...prev, email]);
                          setCcInput('');
                        }
                      }}
                      data-testid="cc-add-btn"
                    >
                      Add
                    </Button>
                  </div>
                  {ccEmails.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="cc-chips">
                      {ccEmails.map((email) => (
                        <span key={email} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
                          {email}
                          <button type="button" onClick={() => setCcEmails(prev => prev.filter(e => e !== email))} className="hover:text-red-600" data-testid={`cc-remove-${email}`}>
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">These contacts will receive updates on this ticket</p>
                </div>

                {/* Linked Assets */}
                <div>
                  <Label>Linked Assets</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Select
                      value=""
                      onValueChange={(assetId) => {
                        const asset = customerAssets.find(a => a.id === assetId);
                        if (asset && !selectedAssets.find(a => a.id === assetId)) {
                          setSelectedAssets(prev => [...prev, asset]);
                        }
                      }}
                    >
                      <SelectTrigger data-testid="asset-select">
                        <SelectValue placeholder={formData.customer_id ? "Select asset" : "Select customer first"} />
                      </SelectTrigger>
                      <SelectContent className="co-scope ticket-ui">
                        {customerAssets
                          .filter(a => !selectedAssets.find(s => s.id === a.id))
                          .map((asset) => (
                            <SelectItem key={asset.id} value={asset.id}>
                              <span className="flex items-center gap-2">
                                <Package className="h-3.5 w-3.5 text-gray-400" />
                                {asset.name} {asset.serial_number ? `(${asset.serial_number})` : ''}
                              </span>
                            </SelectItem>
                          ))}
                        {customerAssets.filter(a => !selectedAssets.find(s => s.id === a.id)).length === 0 && (
                          <SelectItem value="__none" disabled>No assets available</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <ScanAssetLookup
                      onAssetFound={(asset) => {
                        if (!selectedAssets.find(a => a.id === asset.id)) {
                          setSelectedAssets(prev => [...prev, asset]);
                        }
                      }}
                      customerId={formData.customer_id || undefined}
                      customers={customers}
                      alreadyLinkedIds={selectedAssets.map(a => a.id)}
                      buttonVariant="outline"
                      buttonSize="icon"
                    />
                  </div>
                  {selectedAssets.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="asset-chips">
                      {selectedAssets.map((asset) => (
                        <span key={asset.id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium">
                          <Package className="h-3 w-3" />
                          {asset.name}
                          <button type="button" onClick={() => setSelectedAssets(prev => prev.filter(a => a.id !== asset.id))} className="hover:text-red-600" data-testid={`asset-remove-${asset.id}`}>
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Bind assets related to this ticket</p>
                </div>

                {/* Auto-routing suggestion */}
                {routingSuggestion && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="routing-suggestion">
                    <Zap className="h-4 w-4 text-amber-600 flex-shrink-0" />
                    <div className="flex-1 text-sm">
                      <span className="font-medium text-amber-800">Suggested routing</span>
                      <span className="text-amber-700 ml-1">
                        ({routingSuggestion.rule_name}):
                        {routingSuggestion.priority && <> Priority <strong className="capitalize">{routingSuggestion.priority}</strong></>}
                        {routingSuggestion.board_name && <>, Board <strong>{routingSuggestion.board_name}</strong></>}
                        {routingSuggestion.assignee && <>, Assign to <strong>{routingSuggestion.assignee_name || routingSuggestion.assignee}</strong></>}
                      </span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-amber-300 text-amber-700 hover:bg-amber-100"
                      data-testid="apply-routing-btn"
                      onClick={() => {
                        const updates = {};
                        if (routingSuggestion.priority) updates.priority = routingSuggestion.priority;
                        if (routingSuggestion.board_id) updates.board_id = routingSuggestion.board_id;
                        if (routingSuggestion.assignee) updates.assigned_to = routingSuggestion.assignee;
                        setFormData(prev => ({ ...prev, ...updates }));
                        setRoutingSuggestion(null);
                        toast.success('Routing suggestion applied');
                      }}
                    >
                      Apply
                    </Button>
                    <button type="button" onClick={() => setRoutingSuggestion(null)} className="text-amber-400 hover:text-amber-600" data-testid="dismiss-routing-btn">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="priority">Priority</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(value) => setFormData({ ...formData, priority: value })}
                    >
                      <SelectTrigger data-testid="priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="co-scope ticket-ui">
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger data-testid="status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="co-scope ticket-ui">
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="co-scope ticket-ui">
                        <SelectItem value="hardware">Hardware</SelectItem>
                        <SelectItem value="software">Software</SelectItem>
                        <SelectItem value="network">Network</SelectItem>
                        <SelectItem value="security">Security</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Support Type and Board Selection */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="support_type" className="font-semibold">Support Type *</Label>
                    <Select
                      value={formData.support_type}
                      onValueChange={(value) => setFormData({ ...formData, support_type: value })}
                    >
                      <SelectTrigger data-testid="support-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="co-scope ticket-ui">
                        <SelectItem value="remote">
                          <div className="flex items-center gap-2">
                            <Monitor className="h-4 w-4" />
                            <span>Remote Support</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="onsite">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            <span>On-Site Visit</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="hybrid">
                          <div className="flex items-center gap-2">
                            <span>Hybrid (Remote + On-Site)</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">Select if technician needs to visit customer site</p>
                  </div>

                  <div>
                    <Label htmlFor="board_id">Ticket Board</Label>
                    <Select
                      value={formData.board_id || undefined}
                      onValueChange={(value) => setFormData({ ...formData, board_id: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger data-testid="board-select">
                        <SelectValue placeholder="No board (optional)" />
                      </SelectTrigger>
                      <SelectContent className="co-scope ticket-ui">
                        <SelectItem value="none">No Board</SelectItem>
                        {boards.map(board => (
                          <SelectItem key={board.id} value={board.id}>{board.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">e.g., Helpdesk for customer requests</p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => handleDialogChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-ticket-button">
                  Create Ticket
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters and View Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 flex items-center gap-2">
          <Search className="h-5 w-5 text-gray-400 hidden sm:block" />
          <div className="relative w-full sm:max-w-sm">
            <Input
              placeholder="Search tickets..."
              data-testid="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={searchTerm ? 'pr-8' : ''}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                data-testid="clear-search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* UX3.7: Ticket triage quick views */}
          <div className="flex gap-1 flex-wrap" data-testid="triage-views">
            {[
              { label: 'Open', filter: { status: 'open,in_progress', status_mode: 'is' }, testId: 'tv-open' },
              { label: 'Unassigned', filter: { status: 'open', status_mode: 'is', assigned: 'unassigned' }, testId: 'tv-unassigned' },
              { label: 'Critical', filter: { priority: 'critical', priority_mode: 'is' }, testId: 'tv-critical' },
              { label: 'NinjaOne', filter: { source: 'ninjaone' }, testId: 'tv-ninjaone' },
            ].map(view => (
              <button
                key={view.testId}
                onClick={() => {
                  const newFilters = { ...filters };
                  if (view.filter.status) { newFilters.status = view.filter.status; newFilters.status_mode = view.filter.status_mode || 'is'; }
                  if (view.filter.priority) { newFilters.priority = view.filter.priority; newFilters.priority_mode = view.filter.priority_mode || 'is'; }
                  if (view.filter.source) { newFilters.source = view.filter.source; }
                  setFilters(newFilters);
                }}
                className="h-7 px-2.5 text-[11px] rounded-full border bg-white/5 text-[#b1c3ce] border-[#193b4b] hover:bg-white/10 transition-colors"
                data-testid={view.testId}
              >
                {view.label}
              </button>
            ))}
          </div>

          {/* Quick Board Selector */}
          {boards.length > 0 && (
            <Select
              value={filters.board_id || 'all'}
              onValueChange={(value) => setFilters({...filters, board_id: value === 'all' ? '' : value})}
            >
              <SelectTrigger className="w-[140px] sm:w-[180px]" data-testid="quick-board-select">
                <SelectValue placeholder="All Boards" />
              </SelectTrigger>
              <SelectContent className="co-scope ticket-ui">
                <SelectItem value="all">All Boards</SelectItem>
                {boards.map(board => (
                  <SelectItem key={board.id} value={board.id}>{board.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          <Button
            variant={showFilters ? 'default' : 'outline'}
            onClick={() => setShowFilters(!showFilters)}
            data-testid="filter-toggle"
            size="sm"
            className="sm:size-default"
          >
            <Filter className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Filters</span>
            {hasActiveFilters && (
              <Badge className="ml-1 sm:ml-2 bg-blue-600 text-white">{[filters.status, filters.priority, filters.customer_id, filters.board_id, filters.support_type, filters.is_billable, filters.show_merged].filter(Boolean).length}</Badge>
            )}
          </Button>

          <div className="flex gap-1 border rounded-md p-1">
            <Button
              size="sm"
              variant={viewMode === 'card' ? 'default' : 'ghost'}
              onClick={() => setViewMode('card')}
              data-testid="card-view-button"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              onClick={() => setViewMode('list')}
              data-testid="list-view-button"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'board' ? 'default' : 'ghost'}
              onClick={() => setViewMode('board')}
              data-testid="board-view-button"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="18" rx="1" />
                <rect x="14" y="3" width="7" height="18" rx="1" />
              </svg>
            </Button>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="bg-gray-50">
          <CardContent className="p-3 sm:p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
              <div>
                <Label className="text-xs font-semibold mb-1 block">Status</Label>
                <div className="flex gap-1 mb-1.5">
                  <Button
                    size="sm"
                    variant={filters.status_mode === 'is' ? 'default' : 'outline'}
                    onClick={() => setFilters({...filters, status_mode: 'is', status: ''})}
                    className={`h-6 px-2 text-xs ${filters.status_mode === 'is' ? 'bg-[#1a5490] hover:bg-[#0f3a5f]' : ''}`}
                    data-testid="status-mode-is"
                  >
                    is
                  </Button>
                  <Button
                    size="sm"
                    variant={filters.status_mode === 'is_not' ? 'default' : 'outline'}
                    onClick={() => setFilters({...filters, status_mode: 'is_not', status: filters.status_mode === 'is_not' ? filters.status : 'closed,resolved'})}
                    className={`h-6 px-2 text-xs ${filters.status_mode === 'is_not' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                    data-testid="status-mode-is-not"
                  >
                    is not
                  </Button>
                </div>
                {filters.status_mode === 'is' ? (
                  <Select value={filters.status || undefined} onValueChange={(value) => setFilters({...filters, status: value || ''})}>
                    <SelectTrigger className="h-9" data-testid="filter-status">
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent className="co-scope ticket-ui">
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="space-y-1 p-2 border rounded-md bg-white">
                    {[
                      { value: 'open', label: 'Open' },
                      { value: 'in_progress', label: 'In Progress' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'resolved', label: 'Resolved' },
                      { value: 'closed', label: 'Closed' },
                    ].map(s => {
                      const excluded = (filters.status || '').split(',').filter(Boolean);
                      const isChecked = excluded.includes(s.value);
                      return (
                        <label key={s.value} className="flex items-center gap-2 cursor-pointer text-sm py-0.5">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {
                              const current = (filters.status || '').split(',').filter(Boolean);
                              const updated = isChecked
                                ? current.filter(v => v !== s.value)
                                : [...current, s.value];
                              setFilters({...filters, status: updated.join(',')});
                            }}
                            className="rounded border-neutral-300"
                          />
                          <span className={isChecked ? 'line-through text-neutral-400' : ''}>{s.label}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {filters.status && (
                  <p className="text-xs mt-1 text-neutral-500">
                    {filters.status_mode === 'is_not' ? 'Excluding' : 'Showing'}: <span className="font-medium capitalize">{filters.status.split(',').map(s => s.replace('_', ' ')).join(', ')}</span>
                  </p>
                )}
              </div>

              <div>
                <Label className="text-xs font-semibold mb-1 block">Priority</Label>
                <div className="flex gap-1 mb-1.5">
                  <Button
                    size="sm"
                    variant={filters.priority_mode === 'is' ? 'default' : 'outline'}
                    onClick={() => setFilters({...filters, priority_mode: 'is'})}
                    className={`h-6 px-2 text-xs ${filters.priority_mode === 'is' ? 'bg-[#1a5490] hover:bg-[#0f3a5f]' : ''}`}
                    data-testid="priority-mode-is"
                  >
                    is
                  </Button>
                  <Button
                    size="sm"
                    variant={filters.priority_mode === 'is_not' ? 'default' : 'outline'}
                    onClick={() => setFilters({...filters, priority_mode: 'is_not'})}
                    className={`h-6 px-2 text-xs ${filters.priority_mode === 'is_not' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                    data-testid="priority-mode-is-not"
                  >
                    is not
                  </Button>
                </div>
                <Select value={filters.priority || undefined} onValueChange={(value) => setFilters({...filters, priority: value || ''})}>
                  <SelectTrigger className="h-9" data-testid="filter-priority">
                    <SelectValue placeholder={filters.priority_mode === 'is_not' ? "Exclude priority..." : "All priorities"} />
                  </SelectTrigger>
                  <SelectContent className="co-scope ticket-ui">
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
                {filters.priority && (
                  <p className="text-xs mt-1 text-neutral-500">
                    {filters.priority_mode === 'is_not' ? 'Excluding' : 'Showing'}: <span className="font-medium capitalize">{filters.priority}</span>
                  </p>
                )}
              </div>

              <div>
                <Label className="text-xs font-semibold mb-1 block">Customer</Label>
                <Select value={filters.customer_id || undefined} onValueChange={(value) => setFilters({...filters, customer_id: value || ''})}>
                  <SelectTrigger className="h-9 mt-[30px]" data-testid="filter-customer">
                    <SelectValue placeholder="All customers" />
                  </SelectTrigger>
                  <SelectContent className="co-scope ticket-ui">
                    {customers.map(customer => (
                      <SelectItem key={customer.id} value={customer.id}>{customer.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-semibold mb-1 block">Support Type</Label>
                <Select value={filters.support_type || undefined} onValueChange={(value) => setFilters({...filters, support_type: value || ''})}>
                  <SelectTrigger className="h-9 mt-[30px]" data-testid="filter-support-type">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent className="co-scope ticket-ui">
                    <SelectItem value="remote">Remote</SelectItem>
                    <SelectItem value="onsite">On-Site</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-semibold mb-1 block">Billing</Label>
                <div className="flex gap-2">
                  <Select value={filters.is_billable || undefined} onValueChange={(value) => setFilters({...filters, is_billable: value || ''})}>
                    <SelectTrigger className="h-9 mt-[30px]" data-testid="filter-billable">
                      <SelectValue placeholder="All tickets" />
                    </SelectTrigger>
                    <SelectContent className="co-scope ticket-ui">
                      <SelectItem value="true">Billable Only</SelectItem>
                      <SelectItem value="false">Non-Billable</SelectItem>
                    </SelectContent>
                  </Select>
                  {hasActiveFilters && (
                    <Button size="sm" variant="ghost" onClick={clearFilters} className="h-9 mt-[30px] px-2">
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            <div className="mt-3 pt-3 border-t flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={filters.show_merged}
                  onCheckedChange={(checked) => setFilters({...filters, show_merged: checked})}
                  id="show-merged"
                />
                <Label htmlFor="show-merged" className="text-sm cursor-pointer">
                  Show merged tickets
                </Label>
              </div>
              <Button size="sm" variant="outline" onClick={showAllTickets} className="h-7 text-xs" data-testid="show-all-tickets">
                Show All Tickets
              </Button>
              {hasActiveFilters && (
                <Button size="sm" variant="outline" onClick={clearFilters} className="h-7 text-xs">
                  <X className="h-3 w-3 mr-1" />
                  Reset to Default
                </Button>
              )}
              {isDefaultFilter && (
                <span className="text-xs text-neutral-400 italic">Default: hiding closed & resolved tickets</span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <img 
                src="/cipherone-logo.png" 
                alt="Loading" 
                className="h-12 w-12 logo-spin mx-auto mb-2"
              />
              <div>Loading tickets...</div>
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No tickets found. {hasActiveFilters ? 'Try adjusting your filters.' : 'Create your first ticket!'}
            </div>
          ) : viewMode === 'card' ? (
            <div className="space-y-3">
              {getSortedAndFilteredTickets().map((ticket) => (
                <Card 
                  key={ticket.id} 
                  className="hover:shadow-md transition-shadow cursor-pointer" 
                  data-testid="ticket-card"
                  onClick={() => handleTicketClick(ticket)}
                >
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="text-xs sm:text-sm font-mono text-gray-500">{ticket.ticket_number}</span>
                          <Badge className={`text-[10px] sm:text-xs ${getPriorityColor(ticket.priority)}`}>
                            {capitalize(ticket.priority)}
                          </Badge>
                          <Badge className={`text-[10px] sm:text-xs ${getStatusColor(ticket.status)}`}>
                            <span className="flex items-center gap-1">
                              {getStatusIcon(ticket.status)}
                              {capitalize(ticket.status)}
                            </span>
                          </Badge>
                          {ticket.is_billable && (
                            <Badge className="bg-green-100 text-green-800">
                              <DollarSign className="h-3 w-3 mr-1" />
                              Billable
                            </Badge>
                          )}
                          {ticket.support_type === 'onsite' && (
                            <Badge variant="custom" className="bg-purple-100 text-purple-800 hover:bg-purple-200 transition-colors">
                              <MapPin className="h-3 w-3 mr-1" />
                              On-Site
                            </Badge>
                          )}
                          {ticket.support_type === 'remote' && (
                            <Badge variant="custom" className="bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors">
                              <Monitor className="h-3 w-3 mr-1" />
                              Remote
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-semibold text-base sm:text-lg mb-1 line-clamp-2">
                          {ticket.title}
                          {ticket.source && ticket.source !== 'manual' && (
                            <Badge variant="outline" className={`ml-2 text-[10px] ${
                              ticket.source === 'ninjaone' ? 'border-blue-300 text-blue-700 bg-blue-50' :
                              ticket.source === 'ninjarmm_webhook' ? 'border-orange-300 text-orange-700 bg-orange-50' :
                              ticket.source === 'office365_email' ? 'border-purple-300 text-purple-700 bg-purple-50' :
                              'border-gray-300'
                            }`} data-testid={`source-badge-${ticket.id}`}>
                              {ticket.source === 'ninjaone' ? 'NinjaOne' :
                               ticket.source === 'ninjarmm_webhook' ? 'Ninja Alert' :
                               ticket.source === 'office365_email' ? 'Email' :
                               ticket.source}
                            </Badge>
                          )}
                          {ticket.spam_status && (
                            <Badge variant="destructive" className="ml-2 text-[10px]">{ticket.spam_status === 'spam' ? 'Spam' : ticket.spam_status === 'unsolicited' ? 'Unsolicited' : 'Blocked'}</Badge>
                          )}
                          {ticket.attachment_count > 0 && (
                            <Paperclip className="inline h-4 w-4 ml-2 text-neutral-400" title={`${ticket.attachment_count} attachment(s)`} />
                          )}
                          <AssetHealthBadge linkedAssets={ticket.linked_assets} />
                          <SlaBadge ticket={ticket} />
                        </h3>
                        {ticket.description && (
                          <p className="text-sm text-gray-600 mb-2 line-clamp-2">{ticket.description}</p>
                        )}
                        <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 flex-wrap">
                          <span>Customer: {ticket.customer_name || 'N/A'}</span>
                          {ticket.contact_person && <span>• Contact: {ticket.contact_person}</span>}
                          <span>• Created {formatInCST(ticket.created_at, 'MMM d, yyyy, h:mm a')}</span>
                          {ticket.last_response_at && (
                            <span className="text-blue-600 font-medium">
                              • Last response: {formatInCST(ticket.last_response_at, 'MMM d, yyyy, h:mm a')} by {ticket.last_response_by_name} ({capitalize(ticket.last_response_by_role)})
                            </span>
                          )}
                          {ticket.is_billable && ticket.billable_amount > 0 && (
                            <span className="text-green-700 font-semibold">• Bill: ${ticket.billable_amount.toFixed(2)}</span>
                          )}
                        </div>
                      </div>
                      <div className="sm:ml-4 self-start">
                        <Select
                          value={ticket.status}
                          onValueChange={(value) => handleStatusChange(ticket.id, value)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <SelectTrigger className="w-[140px]" data-testid="ticket-status-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="co-scope ticket-ui">
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : viewMode === 'list' ? (
            /* List View */
            <div>
              {selectedTickets.length > 0 && (
                <Card className="mb-4 border-blue-500 bg-blue-50 dark:bg-blue-950/40 dark:border-blue-700">
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-blue-900 text-sm sm:text-base">
                          {selectedTickets.length} ticket(s) selected
                        </span>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => setSelectedTickets([])}
                        >
                          Clear
                        </Button>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {selectedTickets.length >= 2 && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={handleMergeTickets}
                            className="bg-blue-600 text-white hover:bg-blue-700"
                          >
                            Merge
                          </Button>
                        )}
                        <Select onValueChange={handleBulkStatusChange}>
                          <SelectTrigger className="w-[140px] sm:w-[180px] h-9 bg-white">
                            <SelectValue placeholder="Status..." />
                          </SelectTrigger>
                          <SelectContent className="co-scope ticket-ui">
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select onValueChange={async (boardId) => {
                          try {
                            for (const tid of selectedTickets) {
                              await api.put(`/tickets/${tid}`, { board_id: boardId === 'none' ? null : boardId });
                            }
                            toast.success(`Moved ${selectedTickets.length} ticket(s) to ${boardId === 'none' ? 'no board' : boards.find(b => b.id === boardId)?.name || 'board'}`);
                            setSelectedTickets([]);
                            loadTickets();
                          } catch (error) {
                            toast.error('Failed to move tickets');
                          }
                        }}>
                          <SelectTrigger className="w-[140px] sm:w-[180px] h-9 bg-white">
                            <SelectValue placeholder="Board..." />
                          </SelectTrigger>
                          <SelectContent className="co-scope ticket-ui">
                            <SelectItem value="none">No Board</SelectItem>
                            {boards.map(board => (
                              <SelectItem key={board.id} value={board.id}>{board.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="outline" className="text-orange-600 border-orange-300 hover:bg-orange-50" data-testid="bulk-spam-menu">
                              <ShieldBan className="h-4 w-4 sm:mr-2" />
                              <span className="hidden sm:inline">Spam / Block</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="co-scope ticket-ui bg-[#081a25] text-[#f4f8fa] border-[#193b4b] w-52">
                            <DropdownMenuItem onClick={() => handleBulkSpam('spam', false)} className="cursor-pointer text-orange-600" data-testid="bulk-mark-spam">
                              <ShieldBan className="h-4 w-4 mr-2" /> Mark as Spam
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleBulkSpam('unsolicited', false)} className="cursor-pointer text-orange-600" data-testid="bulk-mark-unsolicited">
                              <ShieldBan className="h-4 w-4 mr-2" /> Mark as Unsolicited
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleBulkSpam('spam', true)} className="cursor-pointer text-red-600" data-testid="bulk-spam-block">
                              <Ban className="h-4 w-4 mr-2" /> Spam & Block Senders
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={handleBulkDelete}
                          data-testid="bulk-delete-btn"
                        >
                          <Trash2 className="h-4 w-4 sm:mr-2" />
                          <span className="hidden sm:inline">Delete</span>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Mobile card list view */}
              <div className="block lg:hidden space-y-2">
                {getSortedAndFilteredTickets().map((ticket) => (
                  <div
                    key={ticket.id}
                    className={`border rounded-lg p-3 bg-white dark:bg-neutral-900 hover:shadow-sm transition-shadow ${selectedTickets.includes(ticket.id) ? 'ring-2 ring-blue-400 bg-blue-50 dark:bg-blue-900/30' : ''}`}
                    data-testid="ticket-row-mobile"
                  >
                    <div className="flex items-start gap-2">
                      <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                        <Checkbox 
                          checked={selectedTickets.includes(ticket.id)}
                          onCheckedChange={(checked) => handleSelectTicket(ticket.id, checked)}
                        />
                      </div>
                      <div className="flex-1 min-w-0" onClick={() => handleTicketClick(ticket)}>
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-xs font-mono text-gray-500">{ticket.ticket_number}</span>
                          <Badge className={`text-[10px] px-1.5 py-0 ${getPriorityColor(ticket.priority)}`}>
                            {capitalize(ticket.priority)}
                          </Badge>
                          <Badge className={`text-[10px] px-1.5 py-0 ${getStatusColor(ticket.status)}`}>
                            {capitalize(ticket.status)}
                          </Badge>
                        </div>
                        <h4 className="font-semibold text-sm line-clamp-1">
                          {ticket.title}
                          {ticket.spam_status && (
                            <Badge variant="destructive" className="ml-1 text-[10px]">{ticket.spam_status === 'spam' ? 'Spam' : ticket.spam_status === 'unsolicited' ? 'Unsolicited' : 'Blocked'}</Badge>
                          )}
                          {ticket.attachment_count > 0 && (
                            <Paperclip className="inline h-3 w-3 ml-1 text-neutral-400" />
                          )}
                          <AssetHealthBadge linkedAssets={ticket.linked_assets} />
                        </h4>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 flex-wrap">
                          <span>{ticket.customer_name || 'N/A'}</span>
                          <span>{formatInCST(ticket.created_at, 'MMM d, h:mm a')}</span>
                          {ticket.support_type === 'onsite' && (
                            <Badge className="bg-purple-100 text-purple-800 text-[10px] px-1 py-0">On-Site</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table view */}
              <div className="hidden lg:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <Checkbox 
                        checked={selectedTickets.length === getSortedAndFilteredTickets().length && getSortedAndFilteredTickets().length > 0}
                        onCheckedChange={handleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100"
                      onClick={() => handleColumnSort('ticket_number')}
                    >
                      <div className="flex items-center gap-1">
                        Ticket #
                        {sortBy === 'ticket_number' && (
                          <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100"
                    onClick={() => handleColumnSort('title')}
                  >
                    <div className="flex items-center gap-1">
                      Title
                      {sortBy === 'title' && (
                        <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100"
                    onClick={() => handleColumnSort('customer_name')}
                  >
                    <div className="flex items-center gap-1">
                      Customer
                      {sortBy === 'customer_name' && (
                        <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100"
                    onClick={() => handleColumnSort('status')}
                  >
                    <div className="flex items-center gap-1">
                      Status
                      {sortBy === 'status' && (
                        <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100"
                    onClick={() => handleColumnSort('priority')}
                  >
                    <div className="flex items-center gap-1">
                      Priority
                      {sortBy === 'priority' && (
                        <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead>Billing</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100"
                    onClick={() => handleColumnSort('created_at')}
                  >
                    <div className="flex items-center gap-1">
                      Created
                      {sortBy === 'created_at' && (
                        <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead>Last Response</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getSortedAndFilteredTickets().map((ticket) => (
                  <TableRow 
                    key={ticket.id} 
                    className={`hover:bg-gray-50 dark:hover:bg-neutral-800/40 ${selectedTickets.includes(ticket.id) ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                    data-testid="ticket-row"
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox 
                        checked={selectedTickets.includes(ticket.id)}
                        onCheckedChange={(checked) => handleSelectTicket(ticket.id, checked)}
                        data-testid={`ticket-checkbox-${ticket.id}`}
                      />
                    </TableCell>
                    <TableCell 
                      className="font-mono text-sm cursor-pointer"
                      onClick={() => handleTicketClick(ticket)}
                    >
                      {ticket.ticket_number}
                    </TableCell>
                    <TableCell 
                      className="font-semibold cursor-pointer"
                      onClick={() => handleTicketClick(ticket)}
                    >
                      {ticket.title}
                      {ticket.spam_status && (
                        <Badge variant="destructive" className="ml-1.5 text-[10px]">{ticket.spam_status === 'spam' ? 'Spam' : ticket.spam_status === 'unsolicited' ? 'Unsolicited' : 'Blocked'}</Badge>
                      )}
                      {ticket.attachment_count > 0 && (
                        <Paperclip className="inline h-3.5 w-3.5 ml-1.5 text-neutral-400" title={`${ticket.attachment_count} attachment(s)`} />
                      )}
                      <AssetHealthBadge linkedAssets={ticket.linked_assets} />
                      <SlaBadge ticket={ticket} />
                    </TableCell>
                    <TableCell 
                      className="cursor-pointer"
                      onClick={() => handleTicketClick(ticket)}
                    >
                      {ticket.customer_name || 'N/A'}
                    </TableCell>
                    <TableCell 
                      className="cursor-pointer"
                      onClick={() => handleTicketClick(ticket)}
                    >
                      {ticket.support_type === 'onsite' && (
                        <Badge variant="custom" className="bg-purple-100 text-purple-800 hover:bg-purple-200 transition-colors">
                          <MapPin className="h-3 w-3 mr-1" />
                          On-Site
                        </Badge>
                      )}
                      {ticket.support_type === 'remote' && (
                        <Badge variant="custom" className="bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors">
                          <Monitor className="h-3 w-3 mr-1" />
                          Remote
                        </Badge>
                      )}
                      {ticket.support_type === 'hybrid' && (
                        <Badge variant="custom" className="bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors">Hybrid</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(ticket.status)}>
                        {capitalize(ticket.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getPriorityColor(ticket.priority)}>
                        {capitalize(ticket.priority)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {ticket.is_billable ? (
                        <span className="text-green-700 font-semibold">
                          ${ticket.billable_amount?.toFixed(2) || '0.00'}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {formatInCST(ticket.created_at, 'MMM d, yyyy, h:mm a')}
                    </TableCell>
                    <TableCell className="text-sm">
                      {ticket.last_response_at ? (
                        <div>
                          <div className="text-gray-700">{formatInCST(ticket.last_response_at, 'MMM d, yyyy, h:mm a')}</div>
                          <div className="text-xs text-blue-600">
                            by {ticket.last_response_by_name} ({capitalize(ticket.last_response_by_role)})
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">No responses yet</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
            </div>
          ) : (
            /* Board (Kanban) View */
            <div className="pb-4" data-testid="kanban-board">
              <div className="flex flex-col lg:flex-row gap-4">
                {['open', 'in_progress', 'pending', 'resolved', 'closed'].map((status) => {
                  const statusTickets = getTicketsByStatus(status);
                  return (
                    <div
                      key={status}
                      className="w-full lg:flex-1 lg:min-w-[240px]"
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, status)}
                      data-testid={`kanban-column-${status}`}
                    >
                      <div className="bg-gray-100 rounded-lg p-3 sm:p-4 h-full">
                        <div className="flex items-center justify-between mb-3 sm:mb-4">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-900">{capitalize(status)}</h3>
                            <Badge variant="secondary" className="text-xs">
                              {statusTickets.length}
                            </Badge>
                          </div>
                          {getStatusIcon(status)}
                        </div>
                        
                        <div className="space-y-3 min-h-[200px]">
                          {statusTickets.map((ticket) => (
                            <Card
                              key={ticket.id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, ticket)}
                              onDragEnd={handleDragEnd}
                              onClick={() => handleTicketClick(ticket)}
                              className="cursor-grab active:cursor-grabbing hover:shadow-lg transition-all border-l-4"
                              style={{
                                borderLeftColor: 
                                  ticket.priority === 'critical' ? '#ef4444' :
                                  ticket.priority === 'high' ? '#f97316' :
                                  ticket.priority === 'medium' ? '#eab308' :
                                  '#3b82f6'
                              }}
                              data-testid={`kanban-ticket-${ticket.id}`}
                            >
                              <CardContent className="p-3">
                                <div className="space-y-2">
                                  {/* Header */}
                                  <div className="flex items-start justify-between gap-2">
                                    <span className="text-xs font-mono text-gray-500">{ticket.ticket_number}</span>
                                    <Badge className={getPriorityColor(ticket.priority)}>
                                      {capitalize(ticket.priority)}
                                    </Badge>
                                  </div>
                                  
                                  {/* Title */}
                                  <h4 className="font-semibold text-sm line-clamp-2">
                                    {ticket.title}
                                    {ticket.attachment_count > 0 && (
                                      <Paperclip className="inline h-3 w-3 ml-1 text-neutral-400" />
                                    )}
                                    <AssetHealthBadge linkedAssets={ticket.linked_assets} />
                                    <SlaBadge ticket={ticket} />
                                  </h4>
                                  
                                  {/* Description Preview */}
                                  {ticket.description && (
                                    <p className="text-xs text-gray-600 line-clamp-2">{ticket.description}</p>
                                  )}
                                  
                                  {/* Customer & Contact */}
                                  <div className="text-xs text-gray-500 space-y-1">
                                    <div className="font-medium">{ticket.customer_name || 'No customer'}</div>
                                    {ticket.contact_person && (
                                      <div>Contact: {ticket.contact_person}</div>
                                    )}
                                    {ticket.assigned_to && (
                                      <div>Assigned: {ticket.assigned_to.split('@')[0]}</div>
                                    )}
                                  </div>
                                  
                                  {/* Badges Row */}
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {ticket.support_type === 'onsite' && (
                                      <Badge className="bg-purple-100 text-purple-800 text-xs hover:bg-purple-200 transition-colors">
                                        <MapPin className="h-3 w-3 mr-1" />
                                        On-Site
                                      </Badge>
                                    )}
                                    {ticket.support_type === 'remote' && (
                                      <Badge variant="custom" className="bg-blue-100 text-blue-800 text-xs hover:bg-blue-200 transition-colors">
                                        <Monitor className="h-3 w-3 mr-1" />
                                        Remote
                                      </Badge>
                                    )}
                                    {ticket.is_billable && (
                                      <Badge variant="custom" className="bg-green-100 text-green-800 text-xs hover:bg-green-200 transition-colors">
                                        <DollarSign className="h-3 w-3 mr-1" />
                                        {ticket.billable_amount ? `$${ticket.billable_amount.toFixed(2)}` : 'Billable'}
                                      </Badge>
                                    )}
                                  </div>
                                  
                                  {/* Timestamp */}
                                  <div className="text-xs text-gray-400 pt-1 border-t border-gray-200">
                                    Created {formatInCST(ticket.created_at, 'MMM d, yyyy, h:mm a')}
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                          
                          {statusTickets.length === 0 && (
                            <div className="text-center py-8 text-gray-400 text-sm">
                              No tickets
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Pagination Controls */}
          {!loading && tickets.length > 0 && (
            <div data-testid="tickets-pagination" className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-2">
              <div className="text-xs sm:text-sm text-neutral-500">
                <span>Showing {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, totalTickets)} of {totalTickets}</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-xs sm:text-sm text-neutral-500">Per page:</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(value) => {
                      const newSize = value === 'all' ? 10000 : parseInt(value);
                      setPageSize(newSize);
                      setCurrentPage(0);
                    }}
                  >
                    <SelectTrigger className="w-[80px] h-8" data-testid="page-size-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="co-scope ticket-ui">
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="10000">All</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 0}
                    onClick={() => setCurrentPage(0)}
                    data-testid="pagination-first"
                    className="h-8 px-2 hidden sm:inline-flex"
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 0}
                    onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                    data-testid="pagination-prev"
                    className="h-8 w-8 p-0"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-xs sm:text-sm text-neutral-600 px-1 sm:px-2 min-w-[60px] sm:min-w-[80px] text-center">
                    {currentPage + 1} / {Math.max(1, Math.ceil(totalTickets / pageSize))}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage >= Math.ceil(totalTickets / pageSize) - 1}
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    data-testid="pagination-next"
                    className="h-8 w-8 p-0"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage >= Math.ceil(totalTickets / pageSize) - 1}
                    onClick={() => setCurrentPage(Math.ceil(totalTickets / pageSize) - 1)}
                    data-testid="pagination-last"
                    className="h-8 px-2 hidden sm:inline-flex"
                  >
                    Last
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <TicketDetailDialog 
        ticket={selectedTicket}
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        onUpdate={handleTicketUpdate}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedTickets.length} ticket(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the selected tickets and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmBulkDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-bulk-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Merge Tickets Dialog */}
      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent className="co-scope max-w-2xl">
          <DialogHeader>
            <DialogTitle>Merge Tickets</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>Merging {selectedTickets.length} tickets</strong><br />
                Select which ticket should be the primary (target) ticket. All comments, attachments, and time entries from other tickets will be moved to the target ticket.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Select Target Ticket (Primary) *</Label>
              <Select value={mergeTarget} onValueChange={setMergeTarget}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose the ticket to keep..." />
                </SelectTrigger>
                <SelectContent className="co-scope ticket-ui">
                  {selectedTickets.map(ticketId => {
                    const ticket = tickets.find(t => t.id === ticketId);
                    return ticket ? (
                      <SelectItem key={ticket.id} value={ticket.id}>
                        {ticket.ticket_number} - {ticket.title}
                      </SelectItem>
                    ) : null;
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                This ticket will remain open and contain all merged data
              </p>
            </div>
            
            {mergeTarget && (
              <div className="p-3 bg-gray-50 rounded border">
                <p className="text-sm font-semibold mb-2">What will happen:</p>
                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                  <li>Target ticket: <strong>{tickets.find(t => t.id === mergeTarget)?.ticket_number}</strong> will remain open</li>
                  <li>{selectedTickets.filter(id => id !== mergeTarget).length} other ticket(s) will be closed</li>
                  <li>All comments will be moved to target ticket</li>
                  <li>All attachments will be moved to target ticket</li>
                  <li>All time entries will be moved to target ticket</li>
                  <li>A merge note will be added to target ticket</li>
                  <li>Source tickets will reference the target ticket</li>
                </ul>
              </div>
            )}
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => {
                  setMergeDialogOpen(false);
                  setMergeTarget('');
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmMerge}
                disabled={!mergeTarget}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Merge {selectedTickets.length} Tickets
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}